// Utility to manage docAssist windows across origins
const DocAssistWindowManager = {
  // Unique name/key for the window (used in localStorage)
  WINDOW_KEY: 'docAssistWindow_isOpen',
  STORAGE_CHECK_INTERVAL: 1000, // 1 second
  
  // Initialize the window manager
  init() {
    // Set up event listeners for cross-origin communication
    window.addEventListener('message', this.handleMessage.bind(this));
    
    // Set up storage event listener to detect changes from other tabs/windows
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    
    // Check localStorage on page load
    this.checkLocalStorage();
    
    // Set up periodic checks for localStorage to handle edge cases
    this.intervalId = setInterval(this.checkLocalStorage.bind(this), this.STORAGE_CHECK_INTERVAL);
    
    // When the window is about to close, update localStorage
    window.addEventListener('beforeunload', () => {
      // If this is a docAssist window, mark it as closed
      if (window.name === 'docAssistRules') {
        localStorage.removeItem(this.WINDOW_KEY);
      }
    });
  },
  
  // Check if docAssist window is already open via localStorage
  isWindowOpen() {
    try {
      const windowData = localStorage.getItem(this.WINDOW_KEY);
      if (!windowData) return false;
      
      const data = JSON.parse(windowData);
      return data.isOpen === true;
    } catch (e) {
      console.error('Error checking window state:', e);
      return false;
    }
  },
  
  // Mark the window as open in localStorage
  markWindowOpen() {
    try {
      const data = {
        isOpen: true,
        timestamp: Date.now()
      };
      localStorage.setItem(this.WINDOW_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Error marking window as open:', e);
    }
  },
  
  // Handle messages from other windows
  handleMessage(event) {
    // Verify origin for security (adjust allowed origins as needed)
    // const allowedOrigins = [
    //   'http://20.25.118.115:4200',
    //   // Add other trusted origins if needed
    // ];
    
    // if (!allowedOrigins.includes(event.origin)) {
    //   console.warn('Received message from untrusted origin:', event.origin);
    //   return;
    // }
    
    const { action, data } = event.data;
    
    if (action === 'UPDATE_DOCASSIST') {
      // If this is a docAssist window, update its content
      if (window.name === 'docAssistRules') {
        // Update URL with new parameters
        const currentUrl = new URL(window.location.href);
        const searchParams = new URLSearchParams(currentUrl.search);
        
        // Update parameters
        if (data.packetId) searchParams.set('packetId', data.packetId);
        if (data.orgId) searchParams.set('orgId', data.orgId);
        
        // Create new URL and navigate to it
        currentUrl.search = searchParams.toString();
        window.location.href = currentUrl.toString();
      }
    }
    else if (action === 'CHECK_DOCASSIST_WINDOW') {
      // Respond to window check requests
      if (window.name === 'docAssistRules') {
        event.source.postMessage({
          action: 'DOCASSIST_WINDOW_RESPONSE',
          isOpen: true
        }, event.origin);
      }
    }
  },
  
  // Handle localStorage changes (from other tabs/windows)
  handleStorageChange(event) {
    if (event.key === this.WINDOW_KEY) {
      this.checkLocalStorage();
    }
  },
  
  // Check localStorage and update window status
  checkLocalStorage() {
    // If this is a docAssist window, mark it as open
    if (window.name === 'docAssistRules') {
      this.markWindowOpen();
    }
  },
  
  // Open or update docAssist window
  openOrUpdateDocAssistWindow(docAssistUrl, width, height, left, top) {
    let docAssistWindow = null;
    
    // First check if window is already open according to localStorage
    if (this.isWindowOpen()) {
      // Try to find the window by broadcasting a message to all windows
      const allWindows = window.opener ? [window.opener] : [];
      
      // Include parent window if it exists
      if (window.parent && window.parent !== window) {
        allWindows.push(window.parent);
      }
      
      // Send message to potential parent windows
      allWindows.forEach(w => {
        try {
          w.postMessage({
            action: 'UPDATE_DOCASSIST',
            data: {
              url: docAssistUrl,
              // Parse URL parameters
              packetId: new URLSearchParams(new URL(docAssistUrl).search).get('packetId'),
              orgId: new URLSearchParams(new URL(docAssistUrl).search).get('orgId')
            }
          }, '*');
        } catch (e) {
          console.warn('Failed to send message to potential parent window', e);
        }
      });
      
      // Try to broadcast to all potential windows
      try {
        window.postMessage({
          action: 'UPDATE_DOCASSIST',
          data: {
            url: docAssistUrl,
            packetId: new URLSearchParams(new URL(docAssistUrl).search).get('packetId'),
            orgId: new URLSearchParams(new URL(docAssistUrl).search).get('orgId')
          }
        }, '*');
      } catch (e) {
        console.warn('Failed to broadcast update message', e);
      }
      
      return null; // Don't open a new window
    } else {
      // Open a new window
      docAssistWindow = window.open(
        docAssistUrl,
        "docAssistRules", // fixed window name
        `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );
      
      // Mark window as open in localStorage
      if (docAssistWindow) {
        this.markWindowOpen();
      }
      
      return docAssistWindow;
    }
  }
};

// Export the window manager
export default DocAssistWindowManager;